import React from "react";
import { X, Building2, MapPin, Phone, Clock, Loader2, Users, ShieldCheck, ShieldOff, LogIn } from "lucide-react";

const AVATAR_COLORS = [
  "#F97316", "#3B82F6", "#10B981", "#8B5CF6",
  "#EF4444", "#F59E0B", "#6366F1", "#14B8A6",
];

const getInitials = (name = "") =>
  name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?";

const avatarColor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const planBadgeClass = (tier = "") => {
  switch (tier.toLowerCase()) {
    case "enterprise":   return "bg-purple-100 text-purple-700 border border-purple-200";
    case "professional": return "bg-blue-100   text-blue-700   border border-blue-200";
    default:             return "bg-gray-100   text-gray-600   border border-gray-200";
  }
};

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const InfoRow = ({ icon: Icon, value }) => {
  if (!value || value === "—") return null;
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
      <Icon className="w-4 h-4 text-brand-muted flex-shrink-0" />
      <span className="text-sm text-brand-text">{value}</span>
    </div>
  );
};

const UserProfileModal = ({ user, onClose }) => {
  if (!user) return null;

  const isLoading = !!user._loading;
  const name      = user.name  || "Unknown";
  const email     = user.email || "";
  const plan      = user.plan_tier || "freemium";
  const org       = user.company_name ?? user.company ?? user.organization ?? null;
  const teamSize  = user.team_size  ? `${user.team_size} team members` : null;
  const location  = user.location  ?? user.city ?? null;
  const phone     = user.phone     ?? null;
  const joined    = (user.createdAt || user.created_at) ? `Joined ${formatDate(user.createdAt ?? user.created_at)}` : null;
  const lastLogin = user.last_login ? `Last login ${formatDate(user.last_login)}` : null;
  const verified  = user.is_email_verified != null
    ? (user.is_email_verified ? "Email verified" : "Email not verified") : null;
  const totalAn   = user.totalAnalyses  ?? user.total_analyses   ?? 0;
  const monthAn   = user.this_month_analyses ?? 0;

  const color = avatarColor(name);
  const initials = getInitials(name);
  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 pt-5 pb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-brand-text">User Details</h2>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full text-brand-muted hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Avatar + name */}
        <div className="flex flex-col items-center gap-2 pb-4 px-5">
          <div className="relative">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold select-none"
              style={{ background: color }}
            >
              {initials}
            </div>
            {/* Online dot */}
            <span className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full" />
          </div>
          <div className="text-center">
            <p className="text-base font-bold text-brand-text">{name}</p>
            <p className="text-xs text-brand-muted mt-0.5">{email}</p>
          </div>
          <span className={`text-xs font-medium px-3 py-1 rounded-full ${planBadgeClass(plan)}`}>
            {planLabel}
          </span>
        </div>

        {/* Tab bar */}
        <div className="border-t border-b border-surface-border bg-gray-50/60">
          <button className="w-full py-2.5 text-sm font-semibold text-primary relative">
            Overview
            <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-0.5 bg-primary rounded-t-full" />
          </button>
        </div>

        {/* Info list */}
        <div className="px-6 py-1">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
            </div>
          ) : (
            <>
              <InfoRow icon={Building2}   value={org} />
              <InfoRow icon={Users}       value={teamSize} />
              <InfoRow icon={MapPin}      value={location} />
              <InfoRow icon={Phone}       value={phone} />
              <InfoRow icon={Clock}       value={joined} />
              <InfoRow icon={LogIn}       value={lastLogin} />
              <InfoRow icon={user.is_email_verified ? ShieldCheck : ShieldOff} value={verified} />
            </>
          )}
        </div>

        {/* Stats */}
        <div className="mt-2 grid grid-cols-2 divide-x divide-surface-border border-t border-surface-border bg-indigo-50/40">
          <div className="flex flex-col items-center py-4 gap-0.5">
            <p className="text-2xl font-bold text-brand-text">{totalAn}</p>
            <p className="text-xs text-brand-muted">Overall Analysis</p>
          </div>
          <div className="flex flex-col items-center py-4 gap-0.5">
            <p className="text-2xl font-bold text-brand-text">{monthAn}</p>
            <p className="text-xs text-brand-muted">This Month Analysis</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
